import * as d3_shape from 'd3-shape'
import { isEqual } from 'lodash'
import PropTypes from 'prop-types'

import theme from 'theme'

import BaseGraph from './common/Base'

export const DONUT_ATTACKS_COLOR_MAP = {
  cpu: '#145199',
  shutdown: theme.colors.blue[0],
  blackhole: '#B5D4E9',
  disk: theme.colors.purple[0],
  dns: theme.colors.purple[1],
  io: '#F57300',
  latency: '#7D2E68',
  memory: theme.colors.red[1],
  process_killer: theme.colors.red[0],
  time_travel: theme.colors.yellow[0],
  packet_loss: '#3D348B'
}

export default class DonutChart extends BaseGraph {
  static propTypes = {
    colorMap: PropTypes.object,
    displaySingleData: PropTypes.bool,
    headerText: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    height: PropTypes.number,
    radius: PropTypes.number,
    reportData: PropTypes.array.isRequired,
    rootId: PropTypes.string.isRequired,
    text: PropTypes.string,
    total: PropTypes.number,
    translate: PropTypes.object,
    variant: PropTypes.string
  }
  static defaultProps = {
    colorMap: DONUT_ATTACKS_COLOR_MAP
  }

  componentDidUpdate(previousProps) {
    if (!isEqual(this.props.reportData, previousProps.reportData)) {
      this.redraw()
    }
  }

  draw() {
    const margin = this.margin

    // The activity data
    const report = this.props.reportData
    // This components SVG tag
    const { width, height, g } = this.setReportBasis(report)

    // Draw Donut
    let radius = Math.min(width, this.props.height || height) / 2

    let arc = d3_shape
      .arc()
      .outerRadius(radius)
      .innerRadius(this.props.radius || 50)

    let pie = d3_shape
      .pie()
      .sort(null)
      .value((d) => d.count)

    // Draw background arc if this is a progress variant
    if (this.props.variant === 'progress') {
      const backgroundArc = d3_shape
        .arc()
        .outerRadius(radius)
        .innerRadius(this.props.radius || 50)

      g.selectAll('.background-arc')
        .data(
          pie([
            {
              count: 100
            }
          ])
        )
        .enter()
        .append('g')
        .attr('class', 'background-arc')
        .attr(
          'transform',
          `translate(${(width + margin.left) / 2}, ${height / 2 + margin.top})`
        )

      g.select('.background-arc')
        .append('path')
        .attr('class', 'background')
        .attr('fill', theme.colors.gray[1])
        .attr('d', backgroundArc)
    }

    // Draws pie using d3 shape functionality
    let drawArc = g
      .selectAll('.arc')
      .data(pie(report))
      .enter()
      .append('g')
      .attr('class', 'arc')
      // Update tooltip on graph
      .on('mousemove', (event, data) => {
        return this.updateToolTip(event, data.data)
      })
      // Remove Tooltip
      .on('mouseout', () => this.clearToolTip())
      .attr(
        'transform',
        `translate(${(width + margin.left) / 2}, ${height / 2 + margin.top})`
      )

    // Override the start/endAngle to scale it to the total if this is a progress variant
    if (this.props.variant === 'progress') {
      let tempStart = 0
      let tempEnd = 0

      drawArc
        .append('path')
        .attr(
          'd',
          arc
            .startAngle(() => (tempStart = tempEnd))
            .endAngle(
              (d) =>
                (tempEnd =
                  tempStart + (d.data.count * Math.PI * 2) / this.props.total)
            )
        )
        .style('fill', ({ data }) => this.props.colorMap[data.gremlin])
    } else {
      drawArc
        .append('path')
        .attr('d', arc)
        .style('fill', ({ data }) => this.props.colorMap[data.gremlin])
        .style('stroke', ({ data }) => this.props.colorMap[data.gremlin])
    }

    // In case we want to render `0` as a score - for example
    const renderHeaderText = String(this.props.headerText).length > 0

    if (renderHeaderText) {
      g.selectAll('.background-arc')
        .append('text')
        .attr('text-anchor', 'middle')
        .attr('data-test-donut-header', true)
        .attr('font-size', theme.fontSizes[5])
        .text(this.props.headerText)
    }

    if (this.props.textLine1) {
      g.selectAll('.background-arc')
        .append('text')
        .attr('data-test-donut-text-1', true)
        .attr('text-anchor', 'middle')
        .attr('font-size', theme.fontSizes[0])
        .attr('y', '12%')
        .text(this.props.textLine1)
    }

    if (this.props.textLine2) {
      g.selectAll('.background-arc')
        .append('text')
        .attr('data-test-donut-text-2', true)
        .attr('text-anchor', 'middle')
        .attr('font-size', theme.fontSizes[0])
        .attr('y', '22%')
        .text(this.props.textLine2)
    }

    //Create tooltip
    this.drawToolTip()
  }
}
